import * as React from 'react'
import { useCreateSignedUrlMutation } from '@web/gen/service/creativefabrica/social/v2'
import { socialGetUploadedQueryFetcher } from '@web/gen/feed'
import { useNotificationOnUploadsProcessed } from './use-social-notification'
import type { NotificationPayload } from '@repo/notifications/use-notifications'

export async function fetchUploadedImage(assetId: string) {
  const response = await socialGetUploadedQueryFetcher({
    input: { assetId },
  })()

  if (response.socialGetUploaded?.errors?.length) {
    // oxlint-disable-next-line promise/avoid-new
    await new Promise(resolve => setTimeout(resolve, 2000))
    return fetchUploadedImage(assetId)
  }
  return response.socialGetUploaded
}

export const useFeatureImageUpload = () => {
  const socialCreateSignedUploadUrlMutation = useCreateSignedUrlMutation()
  const [loading, setLoading] = React.useState(false)
  const [trackedUploadIds, setTrackedUploadIds] = React.useState<Set<string>>(new Set())
  const [matchedNotification, setMatchedNotification] = React.useState<NotificationPayload | null>(
    null,
  )
  const { notification } = useNotificationOnUploadsProcessed()

  // Map of uploadId to promise resolvers
  const pendingUploadsRef = React.useRef<
    Map<
      string,
      {
        resolve: (notification: NotificationPayload) => void
        reject: (error: Error) => void
        timeout: NodeJS.Timeout
      }
    >
  >(new Map())

  // Parse notification and check if uploadId matches
  React.useEffect(() => {
    if (!notification) {
      return
    }

    const uploadProcessed = notification.social?.feedPayload?.uploadProcessed?.upload
    const uploadId = uploadProcessed?.id

    if (uploadId && trackedUploadIds.has(uploadId)) {
      setMatchedNotification(notification)

      // Resolve the pending promise for this uploadId
      const pendingUpload = pendingUploadsRef.current.get(uploadId)
      if (pendingUpload) {
        clearTimeout(pendingUpload.timeout)
        pendingUpload.resolve(notification)
        pendingUploadsRef.current.delete(uploadId)
      }
    }
  }, [notification, trackedUploadIds])

  const uploadFeaturedImage = async (file: File): Promise<NotificationPayload> => {
    let uploadId: string | undefined
    setLoading(true)
    try {
      // Get signed URL
      const signedUrlResponse = await socialCreateSignedUploadUrlMutation.mutateAsync({
        fileName: file.name,
        fileSize: BigInt(file.size),
        isImageLibraryUpload: false,
      })

      if (!signedUrlResponse?.url || !signedUrlResponse?.formFields) {
        throw new Error('Failed to get signed URL for featured image upload')
      }

      const { uploadId: responseUploadId, url: signedUrl, formFields } = signedUrlResponse
      uploadId = responseUploadId

      if (!uploadId) {
        throw new Error('Upload ID is missing from signed URL response')
      }

      // Track this uploadId for notification matching
      setTrackedUploadIds(prev => new Set(prev).add(uploadId!))

      // Create a promise that resolves when the notification for this uploadId is received
      // oxlint-disable-next-line promise/avoid-new
      const notificationPromise = new Promise<NotificationPayload>((resolve, reject) => {
        // Set a timeout (e.g., 60 seconds) to prevent hanging forever
        const timeout = setTimeout(() => {
          pendingUploadsRef.current.delete(uploadId!)
          reject(new Error('Timeout waiting for upload notification'))
        }, 60_000)

        pendingUploadsRef.current.set(uploadId!, {
          resolve,
          reject,
          timeout,
        })
      })

      // Create FormData with form fields
      const formData = new FormData()

      // Add form fields from response
      Object.entries(formFields).forEach(([key, value]) => {
        formData.append(key, value)
      })

      // Ensure Content-Type is set
      if (!formFields['Content-Type']) {
        formData.append('Content-Type', file.type)
      }

      // Add the file
      formData.append('file', file)

      // Upload to S3
      await fetch(signedUrl, {
        method: 'POST',
        body: formData,
      })

      // Wait for the notification that indicates the upload is finished
      const matchedNotification = await notificationPromise

      return matchedNotification
    } catch (error) {
      // Clean up any pending upload promises on error
      if (uploadId) {
        const pendingUpload = pendingUploadsRef.current.get(uploadId)
        if (pendingUpload) {
          clearTimeout(pendingUpload.timeout)
          pendingUploadsRef.current.delete(uploadId)
        }
      }
      throw new Error('Failed to upload featured image: ' + (error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const uploadFeaturedImages = async (files: File[]): Promise<NotificationPayload[]> => {
    setLoading(true)
    try {
      const uploadPromises = files.map(async file => {
        return await uploadFeaturedImage(file)
      })
      // oxlint-disable-next-line unicorn/no-await-expression-member
      return (await Promise.allSettled(uploadPromises))
        .filter(result => result.status === 'fulfilled')
        .map(result => (result as PromiseFulfilledResult<NotificationPayload>).value)
    } finally {
      setLoading(false)
    }
  }

  return {
    uploadFeaturedImage,
    uploadFeaturedImages,
    loading,
    matchedNotification,
  }
}
